import { dxtToLocalServerTime, dxtToUTC } from "@dexteel/mesf-core";
import { Button, IconButton } from "@mui/material";
import {
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as UnCheckBoxIcon,
} from "@mui/icons-material";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Link as LinkIcon,
  LinkOff as UnLinkIcon,
  MoreVert as OptionsIcon,
  Restore as RestoreIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import Tippy from "@tippyjs/react";
import type { ICellRendererParams } from "ag-grid-enterprise";
import numeral from "numeral";
import { useRef, useState } from "react";

type Props = ICellRendererParams & {
  dateTimeFormat?: string;
  convertToLocalTime?: boolean;
  numericFormat?: string;
  clicked?: Function;
  isEnabled?: string;
  linkProperty?: string;
  link?: string;
  popUpOptions?: Record<string, string>[];
};
export const ToggleStatusButtonCellRenderer = (props: Props) => {
  if (props.isEnabled && props.data[props.isEnabled] === true)
    return (
      <>
        <DeleteButtonCellRenderer {...props} />
      </>
    );
  else
    return (
      <>
        <ActiveButtonCellRenderer {...props} />
      </>
    );
};
export const ToggleActiveButtonCellRenderer = (props: Props) => {
  if (props.value === true)
    return (
      <>
        <DeleteButtonCellRenderer {...props} />
      </>
    );
  else
    return (
      <>
        <ActiveButtonCellRenderer {...props} />
      </>
    );
};
export const EditButtonCellRenderer = (props: Props) => {
  function btnClickedHandler() {
    (async () => {
      if (props.clicked === undefined) return;
      await props.clicked(props.data);
    })();
  }
  return (
    <>
      <IconButton
        aria-label="edit"
        size="small"
        title="Edit"
        color="primary"
        style={{ cursor: "pointer" }}
        onClick={btnClickedHandler}
      >
        <EditIcon fontSize="inherit" style={{ cursor: "pointer" }} />
      </IconButton>
    </>
  );
};
export const DeleteButtonCellRenderer = (props: Props) => {
  function btnClickedHandler() {
    (async () => {
      if (props.clicked === undefined) return;
      await props.clicked(props.data);
    })();
  }
  return (
    <>
      <IconButton
        aria-label="delete"
        size="small"
        title="Delete"
        style={{ cursor: "pointer" }}
        onClick={btnClickedHandler}
      >
        <DeleteIcon
          fontSize="inherit"
          color="secondary"
          style={{ cursor: "pointer" }}
        />
      </IconButton>
    </>
  );
};
export const ActiveButtonCellRenderer = (props: Props) => {
  function btnClickedHandler() {
    if (props.clicked === undefined) return;
    props.clicked(props.data);
  }
  return (
    <>
      <IconButton
        aria-label="active"
        title="Active"
        size="small"
        style={{ cursor: "pointer" }}
        onClick={btnClickedHandler}
      >
        <RestoreIcon
          fontSize="inherit"
          color="primary"
          style={{ cursor: "pointer" }}
        />
      </IconButton>
    </>
  );
};
export const SendButtonCellRenderer = (props: Props) => {
  function btnClickedHandler() {
    (async () => {
      if (props.clicked === undefined) return;
      await props.clicked(props.data);
    })();
  }
  if (props.data.Sent === true) return <></>;

  if (props.data.ChecksPassed === false) {
    return (
      <>
        <IconButton
          aria-label="edit"
          size="small"
          title="Edit"
          color="primary"
          style={{ cursor: "pointer" }}
          onClick={btnClickedHandler}
        >
          <SendIcon fontSize="inherit" style={{ cursor: "pointer" }} />
        </IconButton>
      </>
    );
  } else return <></>;
};
export const LinkButtonCellRenderer = (props: Props) => {
  function btnClickedHandler() {
    (async () => {
      if (props.clicked === undefined) return;
      await props.clicked(props.data);
    })();
  }
  return (
    <>
      <IconButton
        aria-label="link"
        size="small"
        title="Link"
        color="primary"
        style={{ cursor: "pointer" }}
        onClick={btnClickedHandler}
      >
        <LinkIcon fontSize="inherit" style={{ cursor: "pointer" }} />
      </IconButton>
    </>
  );
};
export const BooleanCellRenderer = (props: Props) => {
  if (props.value === true)
    return (
      <>
        <CheckBoxIcon fontSize="small" color="primary" />
      </>
    );
  return (
    <>
      <UnCheckBoxIcon fontSize="small" color="primary" />
    </>
  );
};
export const BooleanTextCellRenderer = (props: Props) => {
  if (props.value === true)
    return (
      <>
        <span>Y</span>
      </>
    );
  return (
    <>
      <span>F</span>
    </>
  );
};
export const ManualIconCellRenderer = (props: Props) => {
  const isManual = props.data.IsManual;
  return (
    <>
      {isManual ? (
        <div>
          <Button
            aria-label={"Manual"}
            title={"Manual"}
            variant="text"
            size="small"
            sx={{
              borderRadius: "0.25rem",
              backgroundColor: "#D80073",
              width: "25px",
              fontSize: "14px",
              minWidth: "25px",
              height: "25px",
              cursor: "pointer !important",
              border: "1px solid black",
              "&:hover": { backgroundColor: "#D80073" },
              margin: "0px 2px",
              color: "white",
            }}
            style={{ cursor: "auto" }}
          >
            M
          </Button>
        </div>
      ) : (
        <></>
      )}
    </>
  );
};

export const BooleanYNBadgeRenderer = (props: {
  value?: any;
  defaultValue?: boolean;
}) => {
  const isYes = props.value === true;
  const defaultValue = props.defaultValue ?? false;
  const isDefault = props.value === defaultValue;
  const bg = isDefault ? "#9e9e9e" : isYes ? "#2e7d32" : "#d32f2f";
  return (
    <div>
      <Button
        aria-label={isYes ? "Yes" : "No"}
        title={isYes ? "Yes" : "No"}
        variant="text"
        size="small"
        sx={{
          borderRadius: "0.25rem",
          backgroundColor: bg,
          width: "25px",
          fontSize: "14px",
          minWidth: "25px",
          height: "25px",
          cursor: "pointer !important",
          border: "1px solid black",
          "&:hover": { backgroundColor: bg },
          margin: "0px 2px",
          color: "white",
        }}
        style={{ cursor: "auto" }}
      >
        {isYes ? "Y" : "N"}
      </Button>
    </div>
  );
};

export const UnLinkButtonCellRenderer = (props: Props) => {
  function btnClickedHandler() {
    (async () => {
      if (props.clicked === undefined) return;
      await props.clicked(props.data);
    })();
  }
  return (
    <>
      <IconButton
        aria-label="unlink"
        size="small"
        title="Unlink"
        color="primary"
        style={{ cursor: "pointer" }}
        onClick={btnClickedHandler}
      >
        <UnLinkIcon fontSize="inherit" style={{ cursor: "pointer" }} />
      </IconButton>
    </>
  );
};
export const DateTimeCellRenderer = (props: Props) => {
  if (props.value === null || props.value === undefined) return <></>;
  let value;
  if (props.convertToLocalTime) {
    value = dxtToLocalServerTime(props.value, props.dateTimeFormat || "HH:mm");
  } else {
    value = dxtToUTC(props.value, props.dateTimeFormat || "HH:mm");
  }
  return (
    <>
      <span>{value}</span>
    </>
  );
};
export const NumericCellRenderer = (props: Props) => {
  if (props.value === null || props.value === undefined) return <></>;

  const m = numeral(props.value);
  const format = props.numericFormat === "" ? "0.00" : props.numericFormat;
  const formatCell = Number.isNaN(m.value) ? "NaN" : m.format(format);

  return <span>{formatCell}</span>;
};
export const LinkCellRenderer = (props: Props) => {
  if (props.value === null || props.value === undefined) return <></>;
  if (props.link) {
    return (
      <>
        <a href={props.link} target="_self" rel="noopener noreferrer">
          {props.value}
        </a>
      </>
    );
  }
  const link = props.data[props.linkProperty ?? ""] ?? "";
  return (
    <>
      <a href={link} target="_self" rel="noopener noreferrer">
        {props.value}
      </a>
    </>
  );
};
export const LinkAndIconCellRenderer = (props: Props) => {
  if (props.value === null || props.value === undefined) return <></>;
  const link = props.data[props.linkProperty ?? ""] ?? "";
  const isManual = props.data.IsManual;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div>
        <a href={link} target="_self" rel="noopener noreferrer">
          {props.value}
        </a>
      </div>
      {!isManual && (
        <div style={{ padding: "0 2rem" }}>
          <Button
            aria-label={"Manual"}
            title={"Manual"}
            variant="text"
            size="small"
            sx={{
              borderRadius: "0.25rem",
              backgroundColor: "#D80073",
              width: "25px",
              fontSize: "14px",
              minWidth: "25px",
              height: "25px",
              cursor: "pointer !important",
              border: "1px solid black",
              "&:hover": { backgroundColor: "#D80073" },
              margin: "0px 2px",
              color: "white",
            }}
            style={{ cursor: "auto" }}
          >
            M
          </Button>
        </div>
      )}
    </div>
  );
};
export const PopupCellRenderer = (props: Props) => {
  const tippyRef = useRef();
  const [visible, setVisible] = useState(false);
  const show = () => setVisible(true);
  const hide = () => setVisible(false);

  const dropDownContent = (
    <div
      style={{
        zIndex: 1000,
        minWidth: "10rem",
        padding: "0.5rem 0",
        margin: 0,
        fontSize: "1rem",
        textAlign: "left",
        listStyle: "none",
        backgroundColor: "#fff",
        backgroundClip: "padding-box",
        border: "1px solid rgba(0, 0, 0, 0.15)",
        borderRadius: "0.25rem",
      }}
    >
      {props.popUpOptions?.map((record) => (
        <div
          key={record.key}
          onClick={() => onClickHandler(record.key)}
          style={{
            display: "block",
            padding: "0.25rem 1rem",
            clear: "both",
            cursor: "pointer",
          }}
        >
          {record.value}
        </div>
      ))}
    </div>
  );

  const onClickHandler = (option: string) => {
    hide();
    if (props.clicked === undefined) return;
    props.clicked(props.data, option);
  };

  return (
    <Tippy
      content={dropDownContent}
      visible={visible}
      onClickOutside={hide}
      allowHTML={true}
      arrow={false}
      appendTo={document.body}
      interactive={true}
      placement="right"
    >
      <IconButton
        aria-label="active"
        title="Options"
        size="small"
        style={{ cursor: "pointer" }}
        onClick={visible ? hide : show}
      >
        <OptionsIcon
          fontSize="inherit"
          color="primary"
          style={{ cursor: "pointer" }}
        />
      </IconButton>
    </Tippy>
  );
};
